import { Button, Sheet, TextField, Toast } from "@rhema/ui";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReportSheet, type ReportTarget } from "../people/ReportSheet";

type Ask = { title: string; detail: string; action: string };
/** A confirmation with a second way to go ahead, such as signing out but keeping this device's messages. */
type Choice = Ask & { other: string };
type Note = { text: string; undo?: () => void };
type NameAsk = { title: string; label: string; value: string; action: string; placeholder?: string };

export type Feedback = {
  /** Asks before something that cannot be undone. Resolves true when the person goes ahead. */
  confirm: (ask: Ask) => Promise<boolean>;
  /** Like confirm, with a second choice. Resolves "action", "other", or null when the person cancels. */
  choose: (ask: Choice) => Promise<"action" | "other" | null>;
  /** Asks for a short name, such as a conversation's title. Resolves null when the person cancels. */
  name: (ask: NameAsk) => Promise<string | null>;
  /** A short note at the bottom of the screen, with Undo for things that can be taken back. */
  notify: (text: string, undo?: () => void) => void;
  /** Opens the report sheet for a person or a chat. */
  report: (target: ReportTarget) => void;
};

const FeedbackContext = createContext<Feedback | null>(null);

const NOTE_MS = 5000;

/** Confirmations, names, notes with Undo and reports, for the menus anywhere in the app. */
export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [ask, setAsk] = useState<(Ask & { other?: string }) | null>(null);
  const answer = useRef<((choice: "action" | "other" | null) => void) | null>(null);
  const [note, setNote] = useState<Note | null>(null);
  const [reporting, setReporting] = useState<ReportTarget | null>(null);
  const [naming, setNaming] = useState<NameAsk | null>(null);
  const [draft, setDraft] = useState("");
  const named = useRef<((name: string | null) => void) | null>(null);

  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(null), NOTE_MS);
    return () => clearTimeout(t);
  }, [note]);

  const settle = useCallback((choice: "action" | "other" | null) => {
    answer.current?.(choice);
    answer.current = null;
    setAsk(null);
  }, []);

  const settleName = useCallback((name: string | null) => {
    named.current?.(name);
    named.current = null;
    setNaming(null);
  }, []);

  const value = useMemo<Feedback>(
    () => ({
      confirm: (next) =>
        new Promise<boolean>((resolve) => {
          answer.current?.(null);
          answer.current = (choice) => resolve(choice === "action");
          setAsk(next);
        }),
      choose: (next) =>
        new Promise<"action" | "other" | null>((resolve) => {
          answer.current?.(null);
          answer.current = resolve;
          setAsk(next);
        }),
      name: (next) =>
        new Promise<string | null>((resolve) => {
          named.current?.(null);
          named.current = resolve;
          setDraft(next.value);
          setNaming(next);
        }),
      notify: (text, undo) => setNote({ text, ...(undo ? { undo } : {}) }),
      report: (target) => setReporting(target),
    }),
    [],
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <Sheet open={ask !== null} onClose={() => settle(null)} title={ask?.title ?? ""} className="px-5 pb-7">
        <p className="m-0 mt-3 text-[15px] leading-normal text-ink-secondary">{ask?.detail}</p>
        <div className="mt-5 flex flex-col gap-2">
          {ask?.other && (
            <Button size="lg" onClick={() => settle("other")}>
              {ask.other}
            </Button>
          )}
          <Button variant="danger-fill" size="lg" onClick={() => settle("action")}>
            {ask?.action}
          </Button>
          <Button variant="ghost" onClick={() => settle(null)}>
            Cancel
          </Button>
        </div>
      </Sheet>
      <Sheet
        open={naming !== null}
        onClose={() => settleName(null)}
        title={naming?.title ?? ""}
        className="px-5 pb-7"
      >
        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            settleName(draft.trim());
          }}
        >
          <TextField
            label={naming?.label ?? ""}
            placeholder={naming?.placeholder}
            value={draft}
            maxLength={120}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          />
          <Button type="submit" size="lg">
            {naming?.action}
          </Button>
          <Button variant="ghost" onClick={() => settleName(null)}>
            Cancel
          </Button>
        </form>
      </Sheet>
      {note && (
        <div className="pointer-events-none fixed right-0 bottom-24 left-0 z-50 flex justify-center px-4 min-[900px]:bottom-8">
          <Toast className="pointer-events-auto flex items-center gap-3">
            <span>{note.text}</span>
            {note.undo && (
              <button
                type="button"
                onClick={() => {
                  note.undo?.();
                  setNote(null);
                }}
                className="cursor-pointer rounded px-1 font-bold text-gold underline-offset-2 hover:underline"
              >
                Undo
              </button>
            )}
          </Toast>
        </div>
      )}
      {reporting && <ReportSheet open target={reporting} onClose={() => setReporting(null)} />}
    </FeedbackContext.Provider>
  );
}

const quiet: Feedback = {
  confirm: async () => false,
  choose: async () => null,
  name: async () => null,
  notify: () => undefined,
  report: () => undefined,
};

export function useFeedback(): Feedback {
  return useContext(FeedbackContext) ?? quiet;
}
